import React, { useCallback, useMemo, useRef } from 'react';
import bowser from 'yola-bowser';
import classNames from 'classnames';
import Icon from '../icon';
import IconButton from '../icon-button';
import noop from '../../../utils/noop';
import { ALIGN, SIZES, TYPES } from './constants';
const InputGroup = ({
  id = '',
  inputRef = null,
  size = SIZES.small,
  align = ALIGN.left,
  disabled = false,
  value = '',
  defaultValue = '',
  placeholder = '',
  iconGlyph = undefined,
  prefixText = '',
  type = TYPES.text,
  invalid = false,
  selectOnFocus = false,
  onFocus = noop,
  onBlur = noop,
  onChange,
  onClick = undefined,
  iconButtonGlyph = undefined,
  onIconButtonClick = undefined,
  onKeyDown = noop,
  ...restProps
}) => {
  const timer = useRef(null);
  const inputId = useMemo(() => id || Date.now(), [id]);
  const handleChange = useCallback(
    ({ target }) => {
      onChange(target.value);
    },
    [onChange]
  );

  const handleFocus = useCallback(
    ({ target }) => {
      onFocus(target.value);

      if (selectOnFocus) {
        // workaround for safari
        // link: https://stackoverflow.com/a/54229871
        timer.current = setTimeout(() => {
          target.select();
        }, 0);
      }
    },
    [onFocus, selectOnFocus]
  );

  const handleBlur = useCallback(
    ({ target }) => {
      clearTimeout(timer.current);
      onBlur(target.value);
    },
    [onBlur]
  );

  const isMobile = bowser.mobile;
  const isSafari = bowser.safari;
  const isIOS = bowser.ios;
  const isTablet = bowser.tablet;
  const isTouchDevice = isMobile || isTablet;
  const hasIcon = !!iconGlyph;

  const classes = classNames('ws-ui-input-group', `ws-ui-input-group--${size}`, {
    'ws-ui-input-group--disabled': disabled,
    'ws-ui-input-group--invalid': invalid,
    'ws-ui-input-group--mobile': isMobile,
    'ws-ui-input-group--with-icon': hasIcon,
  });

  const hasPrefix = !!prefixText;

  const labelClasses = classNames(`ws-ui-input-group__label--${size}`, 'ws-ui-input-group__label', {
    'ws-ui-input-group__label--disabled': disabled,
    'ws-ui-input-group__label--invalid': invalid,
    'ws-ui-input-group__label--with-prefix': hasPrefix,
    'ws-ui-input-group__label--mobile': isMobile,
    'ws-ui-input-group__label--touch-device': isTouchDevice,
    'ws-ui-input-group__label--no-ellipsis': isSafari || isIOS,
    'ws-ui-input-group__label--cursor-pointer': onClick,
  });

  const inputClasses = classNames(
    'ws-ui-input-group__input',
    `ws-ui-input-group__input--align-${align}`
  );

  return (
    <div className={classes} translate="no">
      {hasIcon && (
        <div className="ws-ui-input-group__icon">
          <Icon type="inherit" glyph={iconGlyph} size="100%" />
        </div>
      )}

      <label htmlFor={`${inputId}`} className={labelClasses}>
        {hasPrefix && (
          <div className="ws-ui-input-group__prefix">
            <bdi>{prefixText}</bdi>
          </div>
        )}
        <div className="ws-ui-input-group__input-wrapper">
          <input
            id={`${inputId}`}
            {...(inputRef && { ref: inputRef })}
            key={defaultValue}
            {...(!value && defaultValue ? { defaultValue } : { value })}
            className={inputClasses}
            type={type}
            onChange={handleChange}
            {...(onClick && { onClick })}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={onKeyDown}
            placeholder={placeholder}
            disabled={disabled}
            {...restProps}
          />

          {iconButtonGlyph && onIconButtonClick && (
            <IconButton
              transparent
              glyph={iconButtonGlyph}
              onClick={onIconButtonClick}
              disabled={disabled}
            />
          )}
        </div>
      </label>
    </div>
  );
};

export default InputGroup;
